import {
  getIndicatorDataApi,
  getSaveComponentDataApi,
} from './fetch'
import { handleOneIndictorToMoreLine } from './funcs'
import { resolveChartColorsLabel } from '@/utils/defaultColorData'
import {
  isLatLngRouteIndicatorDataList,
  buildMapRouteOverlayFromDataList,
  mergeMapRouteOverlayParts,
} from '@/utils/mapRouteGeo'
import { getTitleBarInlineStyle, getBorderTemplateImageStyle } from '@/utils/chartTitlePresets'
import { INIT_RANK_DATA } from '@/utils/defaultComponentConfig'

/** 指标数据行 y 是否为数值（表头行常为列名字符串，如「销售额」） */
function isIndicatorNumericY (y) {
  if (y === null || y === undefined || y === '') return false
  if (typeof y === 'number' && Number.isFinite(y)) return true
  if (typeof y === 'string') {
    const n = Number(y)
    return Number.isFinite(n) && String(y).trim() !== ''
  }
  return false
}

/** 首条是否为 x/y 列标题行（如 { x: \"country\", y: \"销售额\", label: null }） */
function isXYColumnTitleRow (row) {
  return !!(row && !isIndicatorNumericY(row.y))
}

function escapeHtmlForRichIndicator (s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** 富文本指标：dataList 首行常为表头（x 为列名、y 非数值），正文取其后各行的 x，按行输出为段落 HTML */
function indicatorDataListToRichTextHtml (dataList) {
  if (!Array.isArray(dataList) || !dataList.length) return ''
  let start = 0
  if (dataList.length >= 2 && isXYColumnTitleRow(dataList[0])) {
    start = 1
  }
  const lines = []
  for (let i = start; i < dataList.length; i++) {
    const x = dataList[i]?.x
    if (x != null && x !== '') lines.push(String(x))
  }
  if (!lines.length) return ''
  return lines.map(line => `<p>${escapeHtmlForRichIndicator(line)}</p>`).join('')
}

export default {
  props: {
    config: {
      type: Object,
    },
    componentType: {
      type: String,
    },
    globalConfig: {
      type: Object,
    },
    filterParams: {
      type: Object,
    },
    componentStatus: {
      type: String,
      default: 'edit',
    },
  },
  // Promise 不能放进 data()，会被 Vue 响应式处理坏掉，导致 _indicatorReloadQueue 为 undefined
  beforeCreate () {
    this._indicatorReloadQueue = Promise.resolve()
  },
  data () {
    return {
      option: {},
      charts: null,
      timer: null,
      index: -1,
      borderImgUrl: '',
      loading: false,
      indicatorDescription: '数据说明',
      emptyData: false,
      currentRequestId: 0, // 用于追踪请求的ID
    }
  },
  computed: {
    dataSource () {
      return this.config.chartSet.dataSource
    },

    borderStyle () {
      return this.config.chartSet.borderStyle.borderTemplate
    },
    // 数据指标（统一为字符串，避免选项 value 为数字时 includes 误判导致不请求数据）
    selectedIndicatorIdList () {
      const raw = this.config.chartViewData.selectedIndicatorList
      if (!raw?.length) return []
      return raw
        .map(bar => bar?.dataIndicator)
        .filter(v => v !== undefined && v !== null && v !== '')
        .map(v => String(v))
    },
    /** 实际生效的配色 key（组件单独配色优先，否则大屏 chartColors） */
    resolvedChartColorsLabel () {
      return resolveChartColorsLabel(this.config, this.globalConfig)
    },
    titleBarInlineStyle () {
      return getTitleBarInlineStyle(this.config?.chartSet?.titleStyle)
    },
    chartBorderTemplateImageStyle () {
      return getBorderTemplateImageStyle(this.config?.chartSet?.borderStyle)
    },

  },
  created () {
    const ts = this.config?.chartSet?.titleStyle
    if (!ts) return
    if (ts.titleOffsetX === undefined) this.$set(ts, 'titleOffsetX', 0)
    if (ts.titleOffsetY === undefined) this.$set(ts, 'titleOffsetY', 0)
  },
  beforeDestroy () {
    clearInterval(this.timer)
    if (this.charts) {
      this.charts.dispose()
    }
  },
  watch: {
    // // 监听数据源
    dataSource: {
      handler: function (newValue, oldValue) {
        if (newValue === oldValue) return
        if (newValue && !oldValue) {
          this.config.chartViewData.chartData = this.config.chartViewData.chartData ?? this.config.chartViewData.initData
          return
        }
        this.config.chartViewData.chartData = this.config.chartViewData.initData
        if (newValue !== 'indicatorLib') {
          this.config.chartViewData.selectedIndicatorList = []
        }
      },
      deep: true,
      immediate: true,
    },

  },
  methods: {
    /**
     * 编辑页侧栏修改「数据指标」后由父级通过 ref 显式调用。
     * 排队执行：每次切换都会拉当前选中指标；连选时按顺序请求，避免 requestId 互相丢弃。
     */
    reloadIndicatorDataFromConfig () {
      if (this.config?.chartSet?.dataSource !== 'indicatorLib') return Promise.resolve()
      if (!this.selectedIndicatorIdList?.length) return Promise.resolve()
      if (!this._indicatorReloadQueue || typeof this._indicatorReloadQueue.then !== 'function') {
        this._indicatorReloadQueue = Promise.resolve()
      }
      this._indicatorReloadQueue = this._indicatorReloadQueue
        .catch(() => {})
        .then(() => this._runIndicatorReloadQueued())
      return this._indicatorReloadQueue
    },
    async _runIndicatorReloadQueued () {
      if (this.config?.chartSet?.dataSource !== 'indicatorLib') return
      if (!this.selectedIndicatorIdList?.length) return
      try {
        await this.getChartData(this.filterParams, {
          skipStaleAbort: true,
          bustCache: true,
        })
        this.setIndexDescText()
        await this.saveChartData(this.selectedIndicatorIdList)
      } catch (e) {
        this.$error(e)
      }
    },
    // 鼠标移入移出
    mouseEvents () {
      // 鼠标划入
      this.charts.on('mouseover', () => {
        // 停止定时器，清除之前的高亮
        clearInterval(this.timer)
        this.timer = ''
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
      })
      // 鼠标划出重新定时器开始
      this.charts.on('mouseout', () => {
        this.tooltipsActive()
      })
    },

    /**
 * 提示轮播
 * @param {*} dataLength  一次轮播 需要数据高亮的次数
 */
    tooltipsActive () {
      if (this.timer) {
        clearInterval(this.timer)
      }
      const componentType = this.config.chartComponentType
      // 获取 echarts 组件上 tooltip 需要循环的数据长度
      let dataLength = null
      if (['LineChart', 'BarChart'].includes(componentType)) {
        dataLength = this.series[0].data.length
      } else if (['PieChart', 'MapChart'].includes(componentType)) {
        dataLength = this.renderData.length
      } else if (['Rank'].includes(componentType)) {
        dataLength = this.series.data.length
      }
      // 用定时器控制高亮
      this.timer = setInterval(() => {
        // 清除之前的高亮
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        if (this.index === dataLength - 1) {
          this.index = 0
        } else {
          this.index++
        }

        // 当前下标高亮
        this.charts.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: this.index,
        })

        this.charts.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: this.index,
        })
      }, this.config.chartSet.chartStyle.carouselDelay * 1000)
    },
    //  保存组件数据指标
    async saveChartData (selectedIndicatorIdList) {
      await getSaveComponentDataApi({
        chartData: JSON.stringify({
          selectedIndicatorList: selectedIndicatorIdList?.map(item => { return { dataIndicator: item } }) ?? [],
          showIndicator: this.config.chartViewData.showIndicator,
          detailIndicator: this.config.chartViewData.detailIndicator,
        }),
        initData: JSON.stringify(this.config.chartViewData.initData),
        chartDataType: 2,
        chartViewId: this.config.chartViewData.chartViewId,
        id: this.config.chartViewData?.id || null,
        initDataFlag: 0,
      })
    },
    /**
     *  获取/更新所有指标的数据
     *  返回值是第一个指标的标题，用于编辑时修改第一个指标时，设置组件标题用
     */
    async getChartData (filterParams, options = {}) {
      const skipStaleAbort = options.skipStaleAbort === true
      const bustCache = options.bustCache === true
      try {
        this.loading = true
        // 首条指标的 title
        let firstIndexTitle = ''
        if (this.selectedIndicatorIdList?.length > 0) {
          const requestId = ++this.currentRequestId // 增加请求ID

          const requestParams = bustCache && filterParams && typeof filterParams === 'object'
            ? { ...filterParams, _indicatorTs: Date.now() }
            : filterParams

          // 各组件数据回填  需要判断 多指标的情况
          const promiseList = this.selectedIndicatorIdList?.map(indexId => {
            return getIndicatorDataApi(indexId, requestParams, requestId)
          })
          // 获取指标数据
          const resData = await Promise.all(promiseList)
          // 组件已销毁（如已跳转大屏）则不再写回 config，避免旧请求改到新大屏同源结构上
          if (this._isDestroyed) return
          // 侧栏显式刷新时跳过，避免与其它触发源的 requestId 互相丢弃结果
          if (!skipStaleAbort && requestId !== this.currentRequestId) return
          this.indicatorDescription = '数据说明'

          if (this.config.chartComponent === 'mapChart') {
            this.$set(this.config.chartViewData, 'mapRouteOverlay', null)
            let hasMainChartData = false
            let nextYMergeIndex = 1
            const routeOverlayParts = []

            resData.forEach((resItem, index) => {
              if (!resItem?.dataList?.length) {
                if (index === 0 && !hasMainChartData) {
                  this.setEmptyData()
                }
                return
              }
              if (isLatLngRouteIndicatorDataList(resItem.dataList)) {
                routeOverlayParts.push(buildMapRouteOverlayFromDataList(resItem.dataList))
                if (!firstIndexTitle) firstIndexTitle = resItem.indicatorName
                this.indicatorDescription += `<br/>${resItem.indicatorName}: ${resItem.indicatorDescription} `
                return
              }
              if (!hasMainChartData) {
                this.handleSingleIndictorData(resItem.dataList)
                hasMainChartData = true
                firstIndexTitle = resItem.indicatorName
              } else if (!resItem.dataList[0].label) {
                this.setIndictorDataByIndex(resItem.dataList, nextYMergeIndex)
                nextYMergeIndex += 1
              }
              this.indicatorDescription += `<br/>${resItem.indicatorName}: ${resItem.indicatorDescription} `
            })

            const mergedRoute = mergeMapRouteOverlayParts(routeOverlayParts)
            if (mergedRoute) {
              this.$set(this.config.chartViewData, 'mapRouteOverlay', mergedRoute)
            }
            if (!hasMainChartData && routeOverlayParts.length) {
              this.setEmptyData(false)
            }
          } else {
            resData.forEach((resItem, index) => {
              // 没有数据时 填充空数据
              if (resItem?.dataList?.length === 0) {
                if (index === 0) {
                  this.setEmptyData()
                }
                return
              }
              // 获取首条数据时
              if (index === 0) {
                this.handleSingleIndictorData(resItem?.dataList)
                // 设置组件标题
                firstIndexTitle = resItem.indicatorName
              } else {
                // 多指标时
                if (!resItem.dataList[0].label) {
                  if (this.config.chartComponent === 'richText') {
                    // 富文本仅支持单指标，忽略后续序列
                  } else {
                    // 将后续的指标数据一条一条添加到chartViewData
                    this.setIndictorDataByIndex(resItem.dataList, index)
                  }
                }
              }
              // 设置指标说明
              this.indicatorDescription += `<br/>${resItem.indicatorName}: ${resItem.indicatorDescription} `
            })
          }
        }
        return firstIndexTitle
      } catch (e) {
        this.$error(e)
      } finally {
        this.loading = false
      }
    },
    // 设置指标的第一条数据
    handleSingleIndictorData (resData) {
      if (this.config.chartComponent === 'richText') {
        this.config.chartViewData.chartData = indicatorDataListToRichTextHtml(resData)
        return
      }
      if (this.config.chartComponent === 'scrollTable') {
        this.config.chartViewData.chartData = resData
        return
      }
      // 起点/落点/连线 指标在 getChartData 中单独处理，避免误入大区 label 逻辑
      if (this.config.chartComponent === 'mapChart' && isLatLngRouteIndicatorDataList(resData)) {
        return
      }
      // 通过是否有label来判断是否是多指标，但地图数据也需要判断label
      if (resData[0].label) {
        if (this.config.chartComponent === 'mapChart') {
          // >>>>>> 地图数据需要展示所属大区，特殊处理 <<<<<<<<<<<<
          this.config.chartViewData.chartData = resData.map(bar => {
            return {
              x: bar.x,
              y_0: bar.y,
              label: bar.label,
            }
          })
        } else if (this.config.chartComponent === 'rank') {
          // 榜单图依赖表格式 col_0 / col_1（与 INIT_RANK_DATA 一致），多序列取第一条 y
          const lineData = handleOneIndictorToMoreLine(resData.slice())
          if (lineData?.length > 1) {
            const h = lineData[0]
            const header = { ...INIT_RANK_DATA[0] }
            if (h.y_0 !== undefined && h.y_0 !== null && h.y_0 !== '') {
              header.col_1 = String(h.y_0)
            }
            const rows = lineData.slice(1).map(row => ({
              col_0: row.x,
              col_1: row.y_0 !== undefined && row.y_0 !== null && row.y_0 !== '' ? Number(row.y_0) : 0,
            }))
            this.config.chartViewData.chartData = [header, ...rows]
          } else {
            this.setEmptyData()
          }
        } else {
          //  >>>>>>>> 单指标多条数据 <<<<<<<<<
          this.config.chartViewData.chartData = handleOneIndictorToMoreLine(resData)
        }
      } else {
        // >>>>>>>> 单指标 单数据 <<<<<<<<<
        if (this.config.chartComponent === 'rank') {
          // 指标常见 { x, y, label: null }；首行可能为列名表头 { x:'country', y:'销售额' }，需跳过并写入 col_0/col_1 表头
          const header = { ...INIT_RANK_DATA[0] }
          let body = resData
          if (resData.length && isXYColumnTitleRow(resData[0])) {
            header.col_0 = String(resData[0].x != null ? resData[0].x : header.col_0)
            header.col_1 = String(resData[0].y != null ? resData[0].y : header.col_1)
            body = resData.slice(1)
          }
          const rows = body.map(bar => {
            const n = Number(bar.y)
            return {
              col_0: bar.x,
              col_1: Number.isFinite(n) ? n : 0,
            }
          })
          this.config.chartViewData.chartData = [header, ...rows]
        } else {
          this.config.chartViewData.chartData = resData.map(bar => {
            return {
              x: bar.x,
              y_0: bar?.y || 0,
            }
          })
        }
      }
    },
    // 通过指标下标来设置指标数据
    setIndictorDataByIndex (resData, indictorIndex) {
      if (this.config.chartComponent === 'richText') return
      this.config.chartViewData.chartData = this.config.chartViewData.chartData.map((item, index) => {
        const curItem = resData.find(bar => bar.x === item.x)
        if (curItem) {
          item[`y_${indictorIndex}`] = curItem.y
        } else {
          // 若x轴数据不匹配  首行不匹配则填充对应指标的y值，其余数据不匹配补0
          item[`y_${indictorIndex}`] = index === 0 ? resData[0].y : 0
        }
        return item
      })
    },
    // 指标说明文案拼接
    setIndexDescText () {
      this.indicatorDescription = '数据说明'
      this.selectedIndicatorIdList.forEach(bar => {
        const res = this.config.dataIndicatorList?.find(indicator => {
          return String(indicator.value) === String(bar)
        })
        this.indicatorDescription += `<br/>${res?.label}: ${res?.indicatorDescription} `
      })
    },
    // 通过指标的下标来删除指标对应的数据
    delIndictorDataByIdx (delIdx) {
      let maxI = 0
      Object.keys(this.config.chartViewData.chartData).forEach(key => {
        const i = key.split('_')[1]
        maxI = maxI > i ? maxI : i
      })
      this.config.chartViewData.chartData = this.config.chartViewData.chartData
        .map((item, index) => {
          if (maxI === delIdx) {
            // 删除最后一条指标
            delete item[`y_${delIdx}`]
          } else {
            // 删除其中一条指标
            Object.keys(item).forEach((key, idx) => {
              item[`y_${delIdx + idx}`] = item[`y_${delIdx + idx + 1}`]
            })
            delete item[`y_${maxI}`]
          }
          return item
        })
    },
    //  设置空数据
    /** @param {boolean} clearRouteOverlay 地图航线指标叠加层是否一并清空（仅清空表格数据时保留航线） */
    setEmptyData (clearRouteOverlay = true) {
      if (this.config.chartComponent === 'richText') {
        this.config.chartViewData.chartData = ''
        return
      }
      this.config.chartViewData.chartData = []
      if (clearRouteOverlay && this.config.chartComponent === 'mapChart') {
        this.$set(this.config.chartViewData, 'mapRouteOverlay', null)
      }
    },
  },
}
